import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import MasterV2 from "../MasterV2";

import {
  LoadingDiv,
  SweetAlertShow,
} from "../../master/Master";

import { courseList } from "../../../api/Courses";
import { batchList } from "../../../api/Batch";
import { StaffList } from "../../../api/Staff";
import { studentAttendanceListMonthlyV4 } from "../../../api/Attendance";

const ALL_BATCHES = "__all__";

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const monthTitle = (value) => {
  if (!value) return "-";
  const [y, m] = value.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-IN", {
    month: "short",
    year: "numeric",
  });
};

const fullMonthTitle = (value) => {
  if (!value) return "-";
  const [y, m] = value.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-IN", {
    month: "long",
    year: "numeric",
  });
};

const monthKey = (value) => {
  if (!value) return "";
  const raw = String(value);
  const match = raw.match(/^(\d{4})-(\d{2})/);
  if (match) return `${match[1]}-${match[2]}`;

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const safeDate = (value) => {
  if (!value) return "-";
  const raw = String(value);
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[3]}/${match[2]}/${match[1]}`;

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString("en-IN");
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const getAdmissionValue = (student) =>
  student?.admission_date ||
  student?.date_of_admission ||
  student?.admission_at ||
  student?.created_at ||
  student?.latest_course_and_fee?.admission_date ||
  student?.latest_course_and_fee?.created_at ||
  null;

const getStudentId = (student) =>
  String(student?.id ?? student?.student_id ?? student?.user_id ?? "");

const getRows = (response) => {
  const payload = response?.data?.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const monthsBetween = (from, to) => {
  if (!from || !to || from > to) return [];
  const [fy, fm] = from.split("-").map(Number);
  const [ty, tm] = to.split("-").map(Number);
  const result = [];
  let y = fy;
  let m = fm;

  while (y < ty || (y === ty && m <= tm)) {
    result.push(`${y}-${String(m).padStart(2, "0")}`);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return result;
};

const downloadBlob = (content, fileName, type) => {
  const blob = new Blob(["\ufeff", content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export default function AdmissionMonthWiseAttendanceReport() {
  const navigate = useNavigate();
  const location = useLocation();

  const initialParams = useMemo(
    () => new URLSearchParams(location.search),
    // eslint-disable-next-line
    []
  );

  const [filters, setFilters] = useState({
    session_id: initialParams.get("session_id") || "",
    course_id: initialParams.get("course_id") || "",
    batch_id: initialParams.get("batch_id") || "",
    staff_id: initialParams.get("staff_id") || "",
    student_type: initialParams.get("student_type") || "",
    admission_month: initialParams.get("admission_month") || "",
  });

  const initialReportMonths = useMemo(() => {
    const raw = initialParams.get("report_months") || "";
    return raw.split(",").map((x) => x.trim()).filter(Boolean);
  }, [initialParams]);

  const [selectedMonths, setSelectedMonths] = useState(initialReportMonths);
  const [sessions, setSessions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [monthData, setMonthData] = useState({});
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [attendanceView, setAttendanceView] = useState("all");

  const hasBatch = Boolean(filters.batch_id);
  const isAllBatches = filters.batch_id === ALL_BATCHES;

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("sessions") || "[]");
      setSessions(Array.isArray(stored) ? stored : []);
    } catch {
      setSessions([]);
    }
  }, []);

  useEffect(() => {
    if (!sessions.length || filters.session_id) return;
    const active = sessions.find((x) => Number(x?.status) === 1);
    if (active?.id) {
      setFilters((p) => ({ ...p, session_id: String(active.id) }));
    }
  }, [sessions, filters.session_id]);

  useEffect(() => {
    const load = async () => {
      try {
        setFilterLoading(true);
        const [courseRes, staffRes] = await Promise.all([
          courseList(),
          StaffList("status=1"),
        ]);

        setCourses(
          courseRes?.data?.status && Array.isArray(courseRes?.data?.data)
            ? courseRes.data.data
            : []
        );

        setStaffs(
          staffRes?.data?.status && Array.isArray(staffRes?.data?.data)
            ? staffRes.data.data
            : []
        );
      } catch (e) {
        console.error(e);
      } finally {
        setFilterLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const loadBatches = async () => {
      if (!filters.session_id) {
        setBatches([]);
        return;
      }

      try {
        setFilterLoading(true);
        let q = `&status=1&session_id=${encodeURIComponent(filters.session_id)}`;
        if (filters.course_id) {
          q += `&course_id=${encodeURIComponent(filters.course_id)}`;
        }

        const res = await batchList(q);
        setBatches(
          res?.data?.status && Array.isArray(res?.data?.data)
            ? res.data.data
            : []
        );
      } catch (e) {
        console.error(e);
        setBatches([]);
      } finally {
        setFilterLoading(false);
      }
    };

    loadBatches();
  }, [filters.session_id, filters.course_id]);

  const availableReportMonths = useMemo(() => {
    if (!filters.admission_month) return [];
    return monthsBetween(filters.admission_month, currentMonth());
  }, [filters.admission_month]);

  useEffect(() => {
    if (!filters.admission_month) {
      setSelectedMonths([]);
      setMonthData({});
      return;
    }

    setSelectedMonths((previous) => {
      const valid = previous.filter((m) =>
        availableReportMonths.includes(m)
      );

      // New admission month defaults to that month itself.
      return valid.length ? valid : [filters.admission_month];
    });
  }, [filters.admission_month, availableReportMonths]);

  const buildQuery = (reportMonth) => {
    const params = new URLSearchParams();
    params.set("session_id", filters.session_id);
    params.set("month", reportMonth);
    params.set("admission_month", filters.admission_month);

    if (filters.course_id) params.set("course_id", filters.course_id);
    if (filters.staff_id) params.set("staff_id", filters.staff_id);
    if (filters.student_type) {
      params.set("student_type", filters.student_type);
    }
    if (filters.batch_id && filters.batch_id !== ALL_BATCHES) {
      params.set("batch_id", filters.batch_id);
    }

    return `&${params.toString()}`;
  };

  const loadReport = async () => {
    if (!filters.admission_month) {
      SweetAlertShow(
        "info",
        "Select Admission Month",
        "Please select an admission month first."
      );
      return;
    }

    if (!hasBatch) {
      SweetAlertShow(
        "info",
        "Select Batch",
        "Please select a batch or All Batches."
      );
      return;
    }

    if (!selectedMonths.length) {
      SweetAlertShow(
        "info",
        "Select Report Month",
        "Please select at least one report month."
      );
      return;
    }

    try {
      setLoading(true);
      const results = await Promise.all(
        selectedMonths.map(async (m) => {
          const response = await studentAttendanceListMonthlyV4(buildQuery(m));
          return [m, getRows(response)];
        })
      );

      setMonthData(Object.fromEntries(results));
    } catch (error) {
      console.error("Admission month attendance report:", error);
      setMonthData({});
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Unable to load admission month attendance report."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setMonthData({});
  }, [
    filters.session_id,
    filters.course_id,
    filters.batch_id,
    filters.staff_id,
    filters.student_type,
    filters.admission_month,
    selectedMonths,
  ]);

  useEffect(() => {
    if (!filters.session_id) return;

    const params = new URLSearchParams();
    params.set("session_id", filters.session_id);

    if (filters.course_id) params.set("course_id", filters.course_id);
    if (filters.batch_id && filters.batch_id !== ALL_BATCHES) {
      params.set("batch_id", filters.batch_id);
    }
    if (filters.batch_id === ALL_BATCHES) params.set("all_batches", "1");
    if (filters.staff_id) params.set("staff_id", filters.staff_id);
    if (filters.student_type) params.set("student_type", filters.student_type);
    if (filters.admission_month) {
      params.set("admission_month", filters.admission_month);
    }
    if (selectedMonths.length) {
      params.set("report_months", selectedMonths.join(","));
    }

    navigate(
      { pathname: location.pathname, search: `?${params.toString()}` },
      { replace: true }
    );
    // eslint-disable-next-line
  }, [filters, selectedMonths]);

  const analyseMonth = (student, reportMonth) => {
    const attendances = Array.isArray(student?.attendances)
      ? student.attendances
      : [];

    let present = 0;
    let absent = 0;

    attendances.forEach((a) => {
      if (monthKey(a?.date) !== reportMonth) return;
      if (Number(a?.check_in) === 1) present += 1;
      if (Number(a?.check_in) === 2) absent += 1;
    });

    const marked = present + absent;
    const percentage =
      marked > 0 ? Number(((present / marked) * 100).toFixed(1)) : 0;

    return { present, absent, marked, percentage };
  };

  const reportRows = useMemo(() => {
    const map = new Map();

    selectedMonths.forEach((reportMonth) => {
      const students = Array.isArray(monthData[reportMonth])
        ? monthData[reportMonth]
        : [];

      students.forEach((student) => {
        const id = getStudentId(student);
        if (!id) return;

        // Safety fallback: exact admission-month filtering on frontend.
        if (
          filters.admission_month &&
          monthKey(getAdmissionValue(student)) !== filters.admission_month
        ) {
          return;
        }

        if (!map.has(id)) {
          map.set(id, {
            student,
            months: {},
          });
        }

        const entry = map.get(id);
        entry.student = entry.student || student;
        entry.months[reportMonth] = analyseMonth(student, reportMonth);
      });
    });

    return Array.from(map.values()).map((entry) => {
      const totals = selectedMonths.reduce(
        (acc, m) => {
          const stat = entry.months[m] || {
            present: 0,
            absent: 0,
            marked: 0,
          };
          acc.present += stat.present;
          acc.absent += stat.absent;
          acc.marked += stat.marked;
          return acc;
        },
        { present: 0, absent: 0, marked: 0 }
      );

      totals.percentage =
        totals.marked > 0
          ? Number(((totals.present / totals.marked) * 100).toFixed(1))
          : 0;

      return { ...entry, totals };
    });
  }, [monthData, selectedMonths, filters.admission_month]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();

    return reportRows.filter((row) => {
      const s = row.student;
      const searchable = [
        s?.name,
        s?.registration_no,
        s?.phone,
        s?.mobile,
        s?.latest_course_and_fee?.course?.title,
        s?.latest_course_and_fee?.batch?.title,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (q && !searchable.includes(q)) return false;

      if (attendanceView === "low") {
        return row.totals.marked > 0 && row.totals.percentage < 75;
      }
      if (attendanceView === "absent") return row.totals.absent > 0;
      if (attendanceView === "perfect") {
        return row.totals.marked > 0 && row.totals.percentage >= 100;
      }
      return true;
    });
  }, [reportRows, search, attendanceView]);

  const summary = useMemo(() => {
    return reportRows.reduce(
      (acc, row) => {
        acc.students += 1;
        acc.present += row.totals.present;
        acc.absent += row.totals.absent;
        acc.marked += row.totals.marked;
        if (row.totals.marked > 0 && row.totals.percentage < 75) {
          acc.low += 1;
        }
        acc.percentage =
          acc.marked > 0
            ? Number(((acc.present / acc.marked) * 100).toFixed(1))
            : 0;
        return acc;
      },
      {
        students: 0,
        present: 0,
        absent: 0,
        marked: 0,
        low: 0,
        percentage: 0,
      }
    );
  }, [reportRows]);

  const selectedSession = sessions.find(
    (x) => String(x?.id) === String(filters.session_id)
  );
  const selectedCourse = courses.find(
    (x) => String(x?.id) === String(filters.course_id)
  );
  const selectedBatch = batches.find(
    (x) => String(x?.id) === String(filters.batch_id)
  );
  const selectedStaff = staffs.find(
    (x) => String(x?.id) === String(filters.staff_id)
  );

  const toggleMonth = (month) => {
    setSelectedMonths((previous) =>
      previous.includes(month)
        ? previous.filter((x) => x !== month)
        : [...previous, month].sort()
    );
  };

  const selectAllMonths = () => {
    setSelectedMonths([...availableReportMonths]);
  };

  const reset = () => {
    const active = sessions.find((x) => Number(x?.status) === 1);
    setFilters({
      session_id: active?.id ? String(active.id) : "",
      course_id: "",
      batch_id: "",
      staff_id: "",
      student_type: "",
      admission_month: "",
    });
    setSelectedMonths([]);
    setMonthData({});
    setSearch("");
    setAttendanceView("all");
  };

  const exportExcel = () => {
    if (!filteredRows.length) {
      SweetAlertShow("info", "No Data", "No report data is available to export.");
      return;
    }

    const headers = [
      "#",
      "Student",
      "Reg. No.",
      "Admission Date",
      "Mobile",
      "Course",
      "Batch",
      ...selectedMonths.flatMap((m) => [
        `${monthTitle(m)} Present`,
        `${monthTitle(m)} Absent`,
        `${monthTitle(m)} Marked`,
        `${monthTitle(m)} %`,
      ]),
      "Total Present",
      "Total Absent",
      "Total Marked",
      "Overall %",
    ];

    const body = filteredRows.map((row, index) => {
      const s = row.student;
      return [
        index + 1,
        s?.name || "",
        s?.registration_no || "",
        safeDate(getAdmissionValue(s)),
        s?.phone || s?.mobile || "",
        s?.latest_course_and_fee?.course?.title || "",
        s?.latest_course_and_fee?.batch?.title || "",
        ...selectedMonths.flatMap((m) => {
          const stat = row.months[m] || {};
          return [
            stat.present || 0,
            stat.absent || 0,
            stat.marked || 0,
            `${stat.percentage || 0}%`,
          ];
        }),
        row.totals.present,
        row.totals.absent,
        row.totals.marked,
        `${row.totals.percentage}%`,
      ];
    });

    const html = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office"
            xmlns:x="urn:schemas-microsoft-com:office:excel"
            xmlns="http://www.w3.org/TR/REC-html40">
        <head><meta charset="UTF-8"></head>
        <body>
          <table border="1">
            <tr>
              <th colspan="${headers.length}">
                Admission Month Wise Attendance Report -
                ${escapeHtml(fullMonthTitle(filters.admission_month))}
              </th>
            </tr>
            <tr>
              <td colspan="${headers.length}">
                Session: ${escapeHtml(selectedSession?.session || "-")}
                | Course: ${escapeHtml(selectedCourse?.title || "All")}
                | Batch: ${escapeHtml(
                  isAllBatches ? "All Batches" : selectedBatch?.title || "-"
                )}
                | Teacher: ${escapeHtml(selectedStaff?.name || "All")}
                | Report Months: ${escapeHtml(
                  selectedMonths.map(monthTitle).join(", ")
                )}
              </td>
            </tr>
            <tr>${headers.map((h) => `<th>${escapeHtml(h)}</th>`).join("")}</tr>
            ${body
              .map(
                (row) =>
                  `<tr>${row
                    .map((cell) => `<td>${escapeHtml(cell)}</td>`)
                    .join("")}</tr>`
              )
              .join("")}
          </table>
        </body>
      </html>
    `;

    downloadBlob(
      html,
      `admission-month-attendance-${filters.admission_month}.xls`,
      "application/vnd.ms-excel;charset=utf-8;"
    );
  };

  const printReport = () => {
    if (!filteredRows.length) {
      SweetAlertShow("info", "No Data", "No report data is available to print.");
      return;
    }

    const win = window.open("", "_blank", "width=1500,height=900");
    if (!win) {
      SweetAlertShow("info", "Popup Blocked", "Please allow popups and try again.");
      return;
    }

    const monthHeaders = selectedMonths
      .map(
        (m) => `
          <th colspan="4">${escapeHtml(monthTitle(m))}</th>
        `
      )
      .join("");

    const monthSubHeaders = selectedMonths
      .map(() => "<th>P</th><th>A</th><th>M</th><th>%</th>")
      .join("");

    const rows = filteredRows
      .map((row, index) => {
        const s = row.student;
        return `
          <tr>
            <td>${index + 1}</td>
            <td class="left"><strong>${escapeHtml(s?.name || "-")}</strong></td>
            <td>${escapeHtml(s?.registration_no || "-")}</td>
            <td>${escapeHtml(safeDate(getAdmissionValue(s)))}</td>
            <td>${escapeHtml(
              s?.latest_course_and_fee?.batch?.title || "-"
            )}</td>
            ${selectedMonths
              .map((m) => {
                const stat = row.months[m] || {};
                return `
                  <td>${stat.present || 0}</td>
                  <td>${stat.absent || 0}</td>
                  <td>${stat.marked || 0}</td>
                  <td>${stat.percentage || 0}%</td>
                `;
              })
              .join("")}
            <td>${row.totals.present}</td>
            <td>${row.totals.absent}</td>
            <td>${row.totals.marked}</td>
            <td><strong>${row.totals.percentage}%</strong></td>
          </tr>
        `;
      })
      .join("");

    win.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8" />
        <title>Admission Month Wise Attendance Report</title>
        <style>
          @page { size: A3 landscape; margin: 7mm; }
          * { box-sizing: border-box; }
          body { font-family: Arial, sans-serif; color:#222; font-size:9px; }
          h1 { margin:0 0 4px; font-size:18px; }
          .meta { margin-bottom:10px; color:#555; line-height:1.6; }
          .summary { display:flex; gap:6px; margin:8px 0; }
          .summary div { border:1px solid #ccc; padding:5px 10px; }
          table { width:100%; border-collapse:collapse; }
          th,td { border:1px solid #999; padding:3px; text-align:center; }
          th { background:#eee; }
          .left { text-align:left; }
        </style>
      </head>
      <body>
        <h1>Admission Month Wise Attendance Report</h1>
        <div class="meta">
          Admission Month: <strong>${escapeHtml(
            fullMonthTitle(filters.admission_month)
          )}</strong>
          &nbsp; | &nbsp;
          Report Months: <strong>${escapeHtml(
            selectedMonths.map(monthTitle).join(", ")
          )}</strong>
          &nbsp; | &nbsp;
          Batch: <strong>${escapeHtml(
            isAllBatches ? "All Batches" : selectedBatch?.title || "-"
          )}</strong>
        </div>
        <div class="summary">
          <div>Students: <strong>${summary.students}</strong></div>
          <div>Present: <strong>${summary.present}</strong></div>
          <div>Absent: <strong>${summary.absent}</strong></div>
          <div>Low Attendance: <strong>${summary.low}</strong></div>
          <div>Overall: <strong>${summary.percentage}%</strong></div>
        </div>
        <table>
          <thead>
            <tr>
              <th rowspan="2">#</th>
              <th rowspan="2">Student</th>
              <th rowspan="2">Reg. No.</th>
              <th rowspan="2">Admission Date</th>
              <th rowspan="2">Batch</th>
              ${monthHeaders}
              <th colspan="4">Overall</th>
            </tr>
            <tr>
              ${monthSubHeaders}
              <th>P</th><th>A</th><th>M</th><th>%</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
        <script>
          window.onload = function () {
            setTimeout(function () {
              window.focus();
              window.print();
            }, 300);
          };
        </script>
      </body>
      </html>
    `);

    win.document.close();
  };

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar attendance-dashboard admission-attendance-page">
        <div className="layout-container">
          <div className="no-print">
            <Header />
          </div>

          <div className="layout-page">
            <div className="no-print">
              <Nav />
            </div>

            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y admission-container">
                <div className="no-print">
                  <MasterV2 />
                </div>

                <main className="admission-main">
                  <section className="report-title-card">
                    <div className="report-title-left">
                      <span className="report-title-icon">
                        <i className="ri-user-add-line" />
                      </span>
                      <div>
                        <span className="report-kicker">STUDENT ATTENDANCE</span>
                        <h1>Admission Month Wise Attendance</h1>
                        <p>
                          Select an admission month and compare attendance across
                          one or multiple months after admission.
                        </p>
                      </div>
                    </div>

                    <div className="report-actions">
                      <button type="button" onClick={exportExcel}>
                        <i className="ri-file-excel-2-line" />
                        Excel
                      </button>
                      <button type="button" onClick={printReport}>
                        <i className="ri-printer-line" />
                        Print
                      </button>
                    </div>
                  </section>

                  <section className="filter-card">
                    <div className="filter-head">
                      <div>
                        <i className="ri-filter-3-line" />
                        <strong>Report Filters</strong>
                        <span>
                          Admission month and batch are required
                        </span>
                      </div>

                      <button type="button" className="reset-btn" onClick={reset}>
                        <i className="ri-refresh-line" />
                        Reset
                      </button>
                    </div>

                    <div className="filter-grid">
                      <div className="field">
                        <label>Academic Session</label>
                        <div className="control">
                          <i className="ri-graduation-cap-line" />
                          <select
                            value={filters.session_id}
                            onChange={(e) => {
                              setFilters((p) => ({
                                ...p,
                                session_id: e.target.value,
                                course_id: "",
                                batch_id: "",
                              }));
                            }}
                          >
                            <option value="">Select Session</option>
                            {sessions.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.session}
                                {Number(s.status) === 1 ? " - Active" : ""}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="field important">
                        <label>
                          Admission Month <b>*</b>
                        </label>
                        <div className="control">
                          <i className="ri-user-add-line" />
                          <input
                            type="month"
                            max={currentMonth()}
                            value={filters.admission_month}
                            onChange={(e) => {
                              setFilters((p) => ({
                                ...p,
                                admission_month: e.target.value,
                              }));
                            }}
                          />
                        </div>
                        <small>
                          Students admitted only in this month
                        </small>
                      </div>

                      <div className="field">
                        <label>Course</label>
                        <div className="control">
                          <i className="ri-book-open-line" />
                          <select
                            value={filters.course_id}
                            onChange={(e) =>
                              setFilters((p) => ({
                                ...p,
                                course_id: e.target.value,
                                batch_id: "",
                              }))
                            }
                          >
                            <option value="">All Courses</option>
                            {courses.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.title}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="field important">
                        <label>
                          Batch <b>*</b>
                        </label>
                        <div className="control">
                          <i className="ri-group-line" />
                          <select
                            value={filters.batch_id}
                            onChange={(e) =>
                              setFilters((p) => ({
                                ...p,
                                batch_id: e.target.value,
                              }))
                            }
                          >
                            <option value="" disabled>
                              Select Batch
                            </option>
                            <option value={ALL_BATCHES}>All Batches</option>
                            {batches.map((b) => (
                              <option key={b.id} value={b.id}>
                                {b.title}
                                {b?.course?.title
                                  ? ` - ${b.course.title}`
                                  : ""}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="field">
                        <label>Class Teacher</label>
                        <div className="control">
                          <i className="ri-user-star-line" />
                          <select
                            value={filters.staff_id}
                            onChange={(e) =>
                              setFilters((p) => ({
                                ...p,
                                staff_id: e.target.value,
                              }))
                            }
                          >
                            <option value="">All Teachers</option>
                            {staffs.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="field">
                        <label>Student Type</label>
                        <div className="control">
                          <i className="ri-user-settings-line" />
                          <select
                            value={filters.student_type}
                            onChange={(e) =>
                              setFilters((p) => ({
                                ...p,
                                student_type: e.target.value,
                              }))
                            }
                          >
                            <option value="">All Students</option>
                            <option value="hostel">Hostel Students</option>
                            <option value="bus">Bus Students</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="month-selector">
                      <div className="month-selector-head">
                        <div>
                          <strong>Report Months</strong>
                          <span>
                            Only months from the admission month onward can be selected.
                          </span>
                        </div>

                        {availableReportMonths.length > 0 && (
                          <div className="month-selector-actions">
                            <button type="button" onClick={selectAllMonths}>
                              Select All
                            </button>
                            <button
                              type="button"
                              onClick={() => setSelectedMonths([])}
                            >
                              Clear
                            </button>
                          </div>
                        )}
                      </div>

                      {!filters.admission_month ? (
                        <div className="month-placeholder">
                          <i className="ri-calendar-check-line" />
                          Select Admission Month first
                        </div>
                      ) : (
                        <div className="month-chips">
                          {availableReportMonths.map((m) => (
                            <button
                              type="button"
                              key={m}
                              className={
                                selectedMonths.includes(m) ? "selected" : ""
                              }
                              onClick={() => toggleMonth(m)}
                            >
                              <i
                                className={
                                  selectedMonths.includes(m)
                                    ? "ri-checkbox-circle-fill"
                                    : "ri-checkbox-blank-circle-line"
                                }
                              />
                              {monthTitle(m)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="filter-footer">
                      {filterLoading && (
                        <span className="loading-note">
                          <i className="ri-loader-4-line spin" />
                          Loading filters...
                        </span>
                      )}

                      <button
                        type="button"
                        className="generate-btn"
                        disabled={
                          loading ||
                          !filters.admission_month ||
                          !hasBatch ||
                          !selectedMonths.length
                        }
                        onClick={loadReport}
                      >
                        <i className="ri-bar-chart-box-line" />
                        Generate Report
                      </button>
                    </div>
                  </section>

                  {Object.keys(monthData).length > 0 && (
                    <>
                      <section className="summary-grid">
                        <button
                          type="button"
                          className={attendanceView === "all" ? "active" : ""}
                          onClick={() => setAttendanceView("all")}
                        >
                          <span>Students</span>
                          <strong>{summary.students}</strong>
                        </button>
                        {/* <div>
                          <span>Present Marks</span>
                          <strong>{summary.present}</strong>
                        </div>
                        <div>
                          <span>Absent Marks</span>
                          <strong>{summary.absent}</strong>
                        </div> */}
                        <button
                          type="button"
                          className={attendanceView === "low" ? "active danger" : ""}
                          onClick={() =>
                            setAttendanceView(
                              attendanceView === "low" ? "all" : "low"
                            )
                          }
                        >
                          <span>Low Attendance</span>
                          <strong>{summary.low}</strong>
                        </button>
                        <div>
                          <span>Overall Attendance</span>
                          <strong>{summary.percentage}%</strong>
                        </div>
                      </section>

                      <section className="table-card">
                        <div className="table-head">
                          <div>
                            <h2>Admission Month Student Attendance</h2>
                            <p>
                              {fullMonthTitle(filters.admission_month)} admissions •{" "}
                              {selectedMonths.length} report month
                              {selectedMonths.length === 1 ? "" : "s"}
                            </p>
                          </div>

                          <div className="table-tools">
                            <div className="search-box">
                              <i className="ri-search-line" />
                              <input
                                type="text"
                                value={search}
                                placeholder="Search student, reg. no., mobile..."
                                onChange={(e) => setSearch(e.target.value)}
                              />
                            </div>

                            <select
                              value={attendanceView}
                              onChange={(e) => setAttendanceView(e.target.value)}
                            >
                              <option value="all">All Students</option>
                              <option value="low">Below 75%</option>
                              <option value="absent">Has Absence</option>
                              <option value="perfect">100% Attendance</option>
                            </select>
                          </div>
                        </div>

                        <div className="table-scroll">
                          <table className="report-table">
                            <thead>
                              <tr>
                                <th rowSpan="2" className="serial">#</th>
                                <th rowSpan="2" className="student">Student</th>
                                <th rowSpan="2" className="reg">Reg. No.</th>
                                <th rowSpan="2" className="admission">
                                  Admission Date
                                </th>
                                <th rowSpan="2" className="batch">Batch</th>

                                {selectedMonths.map((m) => (
                                  <th key={m} colSpan="4" className="month-head">
                                    {monthTitle(m)}
                                  </th>
                                ))}

                                <th colSpan="4" className="overall-head">
                                  Overall
                                </th>
                              </tr>

                              <tr>
                                {selectedMonths.map((m) => (
                                  <React.Fragment key={m}>
                                    <th className="stat p">P</th>
                                    <th className="stat a">A</th>
                                    <th className="stat m">M</th>
                                    <th className="stat pc">%</th>
                                  </React.Fragment>
                                ))}
                                <th className="stat p">P</th>
                                <th className="stat a">A</th>
                                <th className="stat m">M</th>
                                <th className="stat pc">%</th>
                              </tr>
                            </thead>

                            <tbody>
                              {filteredRows.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={9 + selectedMonths.length * 4}
                                    className="empty"
                                  >
                                    <i className="ri-user-search-line" />
                                    <strong>No students found</strong>
                                    <span>
                                      No students match the selected admission
                                      month and report filters.
                                    </span>
                                  </td>
                                </tr>
                              ) : (
                                filteredRows.map((row, index) => {
                                  const s = row.student;
                                  const batch =
                                    s?.latest_course_and_fee?.batch?.title || "-";
                                  const course =
                                    s?.latest_course_and_fee?.course?.title || "";

                                  return (
                                    <tr
                                      key={getStudentId(s) || index}
                                      className={
                                        row.totals.marked > 0 &&
                                        row.totals.percentage < 75
                                          ? "low-row"
                                          : ""
                                      }
                                    >
                                      <td className="serial">{index + 1}</td>

                                      <td className="student">
                                        <div className="student-info">
                                          <span className="avatar">
                                            {String(s?.name || "?")
                                              .trim()
                                              .split(/\s+/)
                                              .slice(0, 2)
                                              .map((x) => x[0]?.toUpperCase())
                                              .join("")}
                                          </span>
                                          <div>
                                            <strong>{s?.name || "-"}</strong>
                                            <small>
                                              {s?.phone || s?.mobile || course || "-"}
                                            </small>
                                          </div>
                                        </div>
                                      </td>

                                      <td className="reg">
                                        <strong>
                                          {s?.registration_no || "-"}
                                        </strong>
                                        <small>
                                          {s?.phone || s?.mobile || ""}
                                        </small>
                                      </td>

                                      <td className="admission">
                                        <strong>
                                          {safeDate(getAdmissionValue(s))}
                                        </strong>
                                        <small>
                                          {monthTitle(
                                            monthKey(getAdmissionValue(s))
                                          )}
                                        </small>
                                      </td>

                                      <td className="batch">
                                        <strong>{batch}</strong>
                                        <small>{course}</small>
                                      </td>

                                      {selectedMonths.map((m) => {
                                        const stat = row.months[m] || {
                                          present: 0,
                                          absent: 0,
                                          marked: 0,
                                          percentage: 0,
                                        };

                                        return (
                                          <React.Fragment key={m}>
                                            <td className="value present">
                                              {stat.present}
                                            </td>
                                            <td className="value absent">
                                              {stat.absent}
                                            </td>
                                            <td className="value marked">
                                              {stat.marked}
                                            </td>
                                            <td
                                              className={`value percentage ${
                                                stat.marked > 0 &&
                                                stat.percentage < 75
                                                  ? "low"
                                                  : ""
                                              }`}
                                            >
                                              {stat.percentage}%
                                            </td>
                                          </React.Fragment>
                                        );
                                      })}

                                      <td className="value present overall">
                                        {row.totals.present}
                                      </td>
                                      <td className="value absent overall">
                                        {row.totals.absent}
                                      </td>
                                      <td className="value marked overall">
                                        {row.totals.marked}
                                      </td>
                                      <td
                                        className={`value percentage overall ${
                                          row.totals.marked > 0 &&
                                          row.totals.percentage < 75
                                            ? "low"
                                            : ""
                                        }`}
                                      >
                                        {row.totals.percentage}%
                                      </td>
                                    </tr>
                                  );
                                })
                              )}
                            </tbody>
                          </table>
                        </div>

                        <div className="table-footer">
                          Showing <strong>{filteredRows.length}</strong> of{" "}
                          <strong>{reportRows.length}</strong> admitted students.
                          <span>
                            P = Present • A = Absent • M = Marked attendance
                          </span>
                        </div>
                      </section>
                    </>
                  )}

                  {!loading && Object.keys(monthData).length === 0 && (
                    <section className="start-state">
                      <span>
                        <i className="ri-calendar-check-line" />
                      </span>
                      <h2>Admission Month Attendance Report</h2>
                      <p>
                        Select admission month, batch and one or more report
                        months, then click Generate Report.
                      </p>
                    </section>
                  )}
                </main>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .admission-attendance-page {
          min-height: 100vh;
          background: #f6f7fb;
        }

        .admission-container {
          padding-top: 14px !important;
        }

        .admission-main {
          width: 100%;
          max-width: 100%;
          margin: 0 auto;
        }

        .report-title-card,
        .filter-card,
        .table-card,
        .start-state {
          border: 1px solid #e7e9ee;
          border-radius: 12px;
          background: #fff;
          box-shadow: 0 4px 15px rgba(20, 28, 45, 0.045);
        }

        .report-title-card {
          margin-top: 12px;
          padding: 17px 18px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
        }

        .report-title-left {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
        }

        .report-title-icon {
          width: 46px;
          height: 46px;
          flex: 0 0 46px;
          display: grid;
          place-items: center;
          border-radius: 11px;
          background: #f0eaff;
          color: #8255ff;
          font-size: 22px;
        }

        .report-kicker {
          color: #8b94a7;
          font-size: 9px;
          font-weight: 800;
          letter-spacing: .08em;
        }

        .report-title-card h1 {
          margin: 2px 0 3px;
          color: #313646;
          font-size: 20px;
          font-weight: 800;
        }

        .report-title-card p {
          margin: 0;
          color: #8b91a0;
          font-size: 11px;
        }

        .report-actions {
          display: flex;
          gap: 7px;
        }

        .report-actions button,
        .reset-btn {
          min-height: 35px;
          padding: 0 11px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          border: 1px solid #e1e4ea;
          border-radius: 8px;
          background: #fff;
          color: #5c6372;
          font-size: 10px;
          font-weight: 700;
          cursor: pointer;
        }

        .filter-card {
          margin-top: 12px;
          padding: 0 16px 15px;
        }

        .filter-head {
          min-height: 49px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          border-bottom: 1px solid #eff0f3;
        }

        .filter-head > div {
          display: flex;
          align-items: center;
          gap: 7px;
        }

        .filter-head > div > i {
          color: #8255ff;
        }

        .filter-head strong {
          color: #444957;
          font-size: 12px;
        }

        .filter-head span {
          color: #9aa0ad;
          font-size: 9px;
        }

        .filter-grid {
          padding-top: 13px;
          display: grid;
          grid-template-columns: repeat(6, minmax(0, 1fr));
          gap: 10px;
        }

        .field label {
          display: block;
          margin-bottom: 5px;
          color: #606573;
          font-size: 10px;
          font-weight: 750;
        }

        .field label b {
          color: #e5484d;
        }

        .field small {
          display: block;
          margin-top: 4px;
          color: #9aa0ad;
          font-size: 8px;
        }

        .control {
          position: relative;
        }

        .control > i {
          position: absolute;
          left: 10px;
          top: 50%;
          transform: translateY(-50%);
          color: #9097a6;
          font-size: 14px;
          pointer-events: none;
        }

        .control select,
        .control input {
          width: 100%;
          height: 38px;
          padding: 0 9px 0 32px;
          border: 1px solid #dde1e8;
          border-radius: 8px;
          outline: none;
          background: #fff;
          color: #454a58;
          font-size: 10px;
        }

        .control select:focus,
        .control input:focus {
          border-color: #9b7cff;
          box-shadow: 0 0 0 3px rgba(130,85,255,.07);
        }

        .important .control select,
        .important .control input {
          border-color: #cbbcff;
          background: #fcfaff;
        }

        .month-selector {
          margin-top: 13px;
          padding: 11px;
          border: 1px solid #ebe7fb;
          border-radius: 10px;
          background: #fcfbff;
        }

        .month-selector-head {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 10px;
        }

        .month-selector-head > div:first-child {
          display: flex;
          flex-direction: column;
        }

        .month-selector-head strong {
          color: #4a4f5e;
          font-size: 11px;
        }

        .month-selector-head span {
          margin-top: 2px;
          color: #999fad;
          font-size: 8px;
        }

        .month-selector-actions {
          display: flex;
          gap: 5px;
        }

        .month-selector-actions button {
          padding: 5px 8px;
          border: 1px solid #ded8f5;
          border-radius: 7px;
          background: #fff;
          color: #7450d8;
          font-size: 8px;
          font-weight: 750;
          cursor: pointer;
        }

        .month-chips {
          margin-top: 9px;
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }

        .month-chips button {
          padding: 6px 9px;
          display: inline-flex;
          align-items: center;
          gap: 5px;
          border: 1px solid #e0e3e8;
          border-radius: 8px;
          background: #fff;
          color: #687080;
          font-size: 9px;
          font-weight: 700;
          cursor: pointer;
        }

        .month-chips button.selected {
          border-color: #8b5cf6;
          background: #f1eaff;
          color: #7047d8;
        }

        .month-placeholder {
          margin-top: 9px;
          min-height: 45px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          border: 1px dashed #dcdfe6;
          border-radius: 8px;
          color: #999fad;
          font-size: 9px;
        }

        .filter-footer {
          margin-top: 12px;
          display: flex;
          justify-content: flex-end;
          align-items: center;
          gap: 10px;
        }

        .loading-note {
          margin-right: auto;
          color: #8c93a1;
          font-size: 9px;
        }

        .spin {
          display: inline-block;
          animation: admissionSpin .8s linear infinite;
        }

        @keyframes admissionSpin {
          to { transform: rotate(360deg); }
        }

        .generate-btn {
          min-height: 37px;
          padding: 0 14px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          border: 0;
          border-radius: 8px;
          background: #7950f2;
          color: #fff;
          font-size: 10px;
          font-weight: 800;
          cursor: pointer;
          box-shadow: 0 5px 12px rgba(121,80,242,.18);
        }

        .generate-btn:disabled {
          opacity: .45;
          cursor: not-allowed;
          box-shadow: none;
        }

        .summary-grid {
          margin-top: 12px;
          display: grid;
          grid-template-columns: repeat(5, minmax(0, 1fr));
          gap: 8px;
        }

        .summary-grid > div,
        .summary-grid > button {
          min-height: 68px;
          padding: 10px 12px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: flex-start;
          border: 1px solid #e7e9ee;
          border-radius: 10px;
          background: #fff;
          color: #565d6d;
          text-align: left;
        }

        .summary-grid > button {
          cursor: pointer;
        }

        .summary-grid > button.active {
          border-color: #b8a4ff;
          background: #faf8ff;
        }

        .summary-grid span {
          color: #9299a7;
          font-size: 9px;
        }

        .summary-grid strong {
          margin-top: 4px;
          color: #333947;
          font-size: 18px;
        }

        .summary-grid .danger strong {
          color: #d64545;
        }

        .table-card {
          margin-top: 12px;
          overflow: hidden;
        }

        .table-head {
          min-height: 63px;
          padding: 10px 14px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          border-bottom: 1px solid #eceef2;
        }

        .table-head h2 {
          margin: 0;
          color: #3e4452;
          font-size: 13px;
          font-weight: 800;
        }

        .table-head p {
          margin: 3px 0 0;
          color: #999fad;
          font-size: 9px;
        }

        .table-tools {
          display: flex;
          gap: 7px;
        }

        .search-box {
          width: 240px;
          position: relative;
        }

        .search-box i {
          position: absolute;
          left: 9px;
          top: 50%;
          transform: translateY(-50%);
          color: #999fad;
        }

        .search-box input,
        .table-tools select {
          height: 34px;
          border: 1px solid #e0e3e8;
          border-radius: 8px;
          background: #fff;
          color: #555b68;
          font-size: 9px;
          outline: none;
        }

        .search-box input {
          width: 100%;
          padding: 0 9px 0 29px;
        }

        .table-tools select {
          padding: 0 8px;
        }

        .table-scroll {
          width: 100%;
          overflow: auto;
        }

        .report-table {
          width: max-content;
          min-width: 100%;
          border-collapse: separate;
          border-spacing: 0;
        }

        .report-table th {
          padding: 8px 7px;
          border-right: 1px solid #e1e3e8;
          border-bottom: 1px solid #dfe2e7;
          background: #f4f4f7;
          color: #555967;
          font-size: 9px;
          font-weight: 800;
          text-align: center;
          white-space: nowrap;
        }

        .report-table td {
          padding: 7px;
          border-right: 1px solid #eceef1;
          border-bottom: 1px solid #eceef1;
          background: #fff;
          color: #505663;
          font-size: 9px;
          text-align: center;
          vertical-align: middle;
        }

        .report-table tbody tr:hover td {
          background: #fbfbfe;
        }

        .report-table .serial { min-width: 42px; }
        .report-table .student {
          min-width: 210px;
          text-align: left;
        }
        .report-table .reg { min-width: 105px; }
        .report-table .admission { min-width: 110px; }
        .report-table .batch { min-width: 120px; }

        .month-head {
          min-width: 200px;
          background: #eeeaff !important;
          color: #6741d9 !important;
        }

        .overall-head {
          min-width: 210px;
          background: #e9f7ef !important;
          color: #147a48 !important;
        }

        .stat {
          min-width: 50px;
        }

        .stat.p { color: #188754; }
        .stat.a { color: #d64545; }
        .stat.pc { color: #7251d6; }

        .student-info {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .avatar {
          width: 31px;
          height: 31px;
          flex: 0 0 31px;
          display: grid;
          place-items: center;
          border-radius: 8px;
          background: #f0eaff;
          color: #7650dd;
          font-size: 8px;
          font-weight: 850;
        }

        .student-info > div,
        .reg,
        .admission,
        .batch {
          line-height: 1.25;
        }

        .student-info strong,
        .reg strong,
        .admission strong,
        .batch strong {
          display: block;
          color: #414754;
          font-size: 9px;
        }

        .student-info small,
        .reg small,
        .admission small,
        .batch small {
          display: block;
          margin-top: 3px;
          color: #9aa0ad;
          font-size: 7px;
        }

        .value {
          min-width: 50px;
          font-weight: 750;
        }

        .value.present { color: #168453; }
        .value.absent { color: #d34646; }
        .value.marked { color: #666d7a; }
        .value.percentage { color: #6445c6; }
        .value.percentage.low {
          background: #fff1f1;
          color: #cf3f3f;
        }

        .value.overall {
          background: #fbfdfb;
          font-weight: 850;
        }

        .low-row td:first-child {
          box-shadow: inset 3px 0 #ef5b5b;
        }

        .empty {
          min-height: 180px;
          padding: 35px !important;
          text-align: center !important;
        }

        .empty i,
        .empty strong,
        .empty span {
          display: block;
        }

        .empty i {
          color: #a6acb7;
          font-size: 28px;
        }

        .empty strong {
          margin-top: 7px;
          color: #5d6370;
          font-size: 11px;
        }

        .empty span {
          margin-top: 3px;
          color: #9aa0ad;
          font-size: 9px;
        }

        .table-footer {
          min-height: 42px;
          padding: 9px 13px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          border-top: 1px solid #eceef2;
          color: #8d94a2;
          font-size: 9px;
        }

        .table-footer strong {
          color: #555b68;
        }

        .start-state {
          margin-top: 12px;
          min-height: 220px;
          padding: 35px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
        }

        .start-state > span {
          width: 52px;
          height: 52px;
          display: grid;
          place-items: center;
          border-radius: 13px;
          background: #f0eaff;
          color: #8255ff;
          font-size: 24px;
        }

        .start-state h2 {
          margin: 11px 0 4px;
          color: #484e5b;
          font-size: 14px;
        }

        .start-state p {
          margin: 0;
          color: #999fad;
          font-size: 10px;
        }

        @media (max-width: 1399px) {
          .filter-grid {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
        }

        @media (max-width: 991px) {
          .filter-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .summary-grid {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }

          .table-head {
            align-items: flex-start;
            flex-direction: column;
          }

          .table-tools {
            width: 100%;
          }

          .search-box {
            flex: 1;
            width: auto;
          }
        }

        @media (max-width: 767px) {
          .admission-container {
            padding-left: 10px !important;
            padding-right: 10px !important;
          }

          .report-title-card {
            align-items: flex-start;
            flex-direction: column;
          }

          .report-actions {
            width: 100%;
          }

          .report-actions button {
            flex: 1;
            justify-content: center;
          }

          .filter-grid {
            grid-template-columns: 1fr;
          }

          .filter-head,
          .month-selector-head {
            align-items: flex-start;
          }

          .filter-head span {
            display: none;
          }

          .month-selector-head {
            flex-direction: column;
          }

          .summary-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .table-tools {
            flex-direction: column;
          }

          .table-tools select,
          .search-box {
            width: 100%;
          }

          .table-footer {
            align-items: flex-start;
            flex-direction: column;
          }
        }

        @media (max-width: 480px) {
          .summary-grid {
            grid-template-columns: 1fr 1fr;
          }

          .report-title-card h1 {
            font-size: 17px;
          }
        }

        @media print {
          .no-print {
            display: none !important;
          }
        }
      `}</style>
    
      {/* Attendance-only layout fix: remove fixed-template navbar offset. */}
      <style>{`
        html.layout-navbar-fixed .attendance-dashboard.layout-wrapper,
        html.layout-navbar-fixed .attendance-dashboard.layout-wrapper > .layout-container,
        .attendance-dashboard.layout-wrapper > .layout-container {
          padding-top: 0 !important;
          margin-top: 0 !important;
        }
        html.layout-navbar-fixed .attendance-dashboard.layout-wrapper .layout-page,
        html.layout-navbar-fixed .layout-wrapper:not(.layout-without-menu).attendance-dashboard .layout-page,
        .attendance-dashboard.layout-wrapper .layout-page {
          padding-top: 0 !important;
          margin-top: 0 !important;
          min-width: 0;
        }
        .attendance-dashboard.layout-wrapper #layout-navbar.scai-topbar,
        html.layout-navbar-fixed .attendance-dashboard.layout-wrapper #layout-navbar {
          top: 0 !important;
          left: auto !important;
          right: auto !important;
          width: 100% !important;
          max-width: none !important;
          margin: 0 !important;
          padding: 0 !important;
          border-radius: 0 !important;
        }
        @media print {
          .attendance-dashboard.layout-wrapper #layout-navbar { display: none !important; }
        }
      `}</style>
</>
  );
}
